/**
 * Color Swatches Component
 * Handles color swatch interactions on product pages
 * Directly updates the form's variant ID for reliable cart additions
 */

class ColorSwatches {
  constructor() {
    this.selectors = {
      colorSelector: '.color-selector',
      swatch: '.color-swatch',
      colorValue: '[data-color-value]',
      variantPicker: 'variant-picker',
      productForm: 'product-form-component form, form[action*="/cart/add"]',
      variantInput: 'input[name="id"]',
      sizeDropdown: '[data-size-select]',
      sizeSelector: '.size-selector'
    };

    this.init();
  }

  init() {
    document.querySelectorAll(this.selectors.colorSelector).forEach((selector) => {
      this.bindSwatchEvents(selector);
    });

    // Listen for variant updates to sync our swatches with external changes
    document.addEventListener('variant:update', this.handleVariantUpdate.bind(this));
  }

  bindSwatchEvents(selector) {
    const swatches = selector.querySelectorAll(this.selectors.swatch);

    swatches.forEach((swatch) => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleSwatchClick(swatch, selector);
      });
    });
  }

  handleSwatchClick(swatch, selector) {
    const colorName = swatch.dataset.color;
    const productId = selector.dataset.productId;

    if (!colorName) return;

    // Update our swatch UI immediately for responsiveness
    this.updateActiveState(selector, swatch);
    this.updateColorDisplay(selector, colorName);

    // Get the currently selected size
    const selectedSize = this.getSelectedSize();

    // Find the correct variant ID for this color+size combination
    const variantId = this.findVariantId(productId, colorName, selectedSize);

    if (variantId) {
      // Update URL without page reload
      this.updateURL(variantId);

      // Directly update the form's variant input
      this.updateFormVariantId(variantId);

      // Trigger variant picker for price/image updates (may cause async form update)
      this.triggerVariantPicker(colorName);

      // Update form again after a short delay to ensure our value takes precedence
      // over any async updates from the theme's variant picker
      setTimeout(() => {
        this.updateFormVariantId(variantId);
      }, 100);
    } else {
      // No variant ID found, just trigger variant picker
      this.triggerVariantPicker(colorName);
    }
  }

  /**
   * Get the currently selected size from the size dropdown or variant picker
   */
  getSelectedSize() {
    // First, try to get from our size selector dropdown
    const sizeDropdown = document.querySelector(this.selectors.sizeDropdown);
    if (sizeDropdown && sizeDropdown.value) {
      return sizeDropdown.value;
    }

    // Try to get from variant picker's size option
    const variantPicker = document.querySelector(this.selectors.variantPicker);
    if (variantPicker) {
      const fieldsets = variantPicker.querySelectorAll('fieldset');
      for (const fieldset of fieldsets) {
        const legend = fieldset.querySelector('legend');
        if (!legend) continue;

        const legendText = legend.textContent.toLowerCase().trim();
        const optionName = legendText.split(/\s+/)[0];

        if (optionName === 'size') {
          const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
          if (checkedRadio) {
            return checkedRadio.value;
          }
        }
      }

      // Try dropdown select
      const selects = variantPicker.querySelectorAll('select');
      for (const select of selects) {
        const label = variantPicker.querySelector(`label[for="${select.id}"]`);
        if (!label) continue;

        const labelText = label.textContent.toLowerCase().trim();
        if (labelText === 'size') {
          return select.value;
        }
      }
    }

    return null;
  }

  /**
   * Find the variant ID for a specific color+size combination
   */
  findVariantId(productId, color, size) {
    // Get variant data from the page
    const variantData = this.getVariantData();

    if (!variantData || !variantData.length) {
      // Fallback: use variant ID from the swatch data attribute
      const swatch = document.querySelector(`[data-color="${color}"]`);
      if (swatch && swatch.dataset.variantId) {
        return swatch.dataset.variantId;
      }
      return null;
    }

    const colorLower = color.toLowerCase();
    const sizeLower = size ? size.toLowerCase() : null;

    // Find variant matching both color and size
    for (const variant of variantData) {
      const options = variant.options || [];
      const optionsLower = options.map(opt => opt ? opt.toLowerCase() : '');

      const hasColor = optionsLower.includes(colorLower);
      const hasSize = !sizeLower || optionsLower.includes(sizeLower);

      if (hasColor && hasSize) {
        return variant.id;
      }
    }

    // If no exact match, find any variant with this color
    for (const variant of variantData) {
      const options = variant.options || [];
      const optionsLower = options.map(opt => opt ? opt.toLowerCase() : '');

      if (optionsLower.includes(colorLower)) {
        return variant.id;
      }
    }

    return null;
  }

  /**
   * Get variant data from the product JSON on the page
   */
  getVariantData() {
    // First, try to get from color-selector's own variant data
    const colorSelector = document.querySelector(this.selectors.colorSelector);
    if (colorSelector) {
      const variantsJson = colorSelector.querySelector('[data-product-variants]');
      if (variantsJson) {
        try {
          return JSON.parse(variantsJson.textContent);
        } catch (e) {
          // Continue to fallback
        }
      }
    }

    // Try to get from size-selector's variant data
    const sizeSelector = document.querySelector(this.selectors.sizeSelector);
    if (sizeSelector) {
      const variantsJson = sizeSelector.querySelector('[data-product-variants]');
      if (variantsJson) {
        try {
          return JSON.parse(variantsJson.textContent);
        } catch (e) {
          // Continue to fallback
        }
      }
    }

    // Try to get from product JSON script tag
    const productJson = document.querySelector('[type="application/json"][data-product-json]');
    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        return product.variants;
      } catch (e) {
        // Continue to fallback
      }
    }

    // Try to get from variant picker's JSON
    const variantPicker = document.querySelector(this.selectors.variantPicker);
    if (variantPicker) {
      const jsonScript = variantPicker.querySelector('script[type="application/json"]');
      if (jsonScript) {
        try {
          const data = JSON.parse(jsonScript.textContent);
          if (Array.isArray(data)) {
            return data;
          }
        } catch (e) {
          // Continue to fallback
        }
      }
    }

    return null;
  }

  /**
   * Directly update the form's variant ID input
   */
  updateFormVariantId(variantId) {
    // Find the product form
    const forms = document.querySelectorAll(this.selectors.productForm);

    forms.forEach(form => {
      const variantInput = form.querySelector(this.selectors.variantInput);
      if (variantInput) {
        variantInput.value = variantId;
      }
    });

    // Also update any standalone variant inputs in product-form-component
    const productFormComponent = document.querySelector('product-form-component');
    if (productFormComponent) {
      const variantInput = productFormComponent.querySelector(this.selectors.variantInput);
      if (variantInput) {
        variantInput.value = variantId;
      }
    }
  }

  /**
   * Update the URL with the new variant ID
   */
  updateURL(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Find the color option in the variant picker and trigger its change
   */
  triggerVariantPicker(colorName) {
    const variantPicker = document.querySelector(this.selectors.variantPicker);

    if (!variantPicker) {
      return;
    }

    // Find all fieldsets (each represents an option like Color, Size, etc.)
    const fieldsets = variantPicker.querySelectorAll('fieldset');

    for (const fieldset of fieldsets) {
      // Check if this fieldset is for color by looking at the legend
      const legend = fieldset.querySelector('legend');
      if (!legend) continue;

      const legendText = legend.textContent.toLowerCase().trim();
      // Remove any swatch value text that might be appended
      const optionName = legendText.split(/\s+/)[0];

      if (optionName === 'color' || optionName === 'colour') {
        // Found the color fieldset, now find the matching radio input
        const radioInputs = fieldset.querySelectorAll('input[type="radio"]');

        for (const radio of radioInputs) {
          // Match by value attribute (the color name)
          if (radio.value.toLowerCase() === colorName.toLowerCase()) {
            // Check the radio and dispatch change event
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
        }
        return;
      }
    }

    // If no fieldset found, try dropdown select
    const selects = variantPicker.querySelectorAll('select');
    for (const select of selects) {
      const label = variantPicker.querySelector(`label[for="${select.id}"]`);
      if (!label) continue;

      const labelText = label.textContent.toLowerCase().trim();
      if (labelText === 'color' || labelText === 'colour') {
        // Found color dropdown, select the matching option
        for (const option of select.options) {
          if (option.value.toLowerCase() === colorName.toLowerCase()) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
        }
      }
    }
  }

  updateActiveState(selector, activeSwatch) {
    // Remove active class from all swatches
    selector.querySelectorAll(this.selectors.swatch).forEach((swatch) => {
      swatch.classList.remove('color-swatch--active');
      swatch.setAttribute('aria-pressed', 'false');
    });

    // Add active class to clicked swatch
    activeSwatch.classList.add('color-swatch--active');
    activeSwatch.setAttribute('aria-pressed', 'true');
  }

  updateColorDisplay(selector, colorName) {
    const colorValueEl = selector.querySelector(this.selectors.colorValue);
    if (colorValueEl) {
      colorValueEl.textContent = colorName;
    }
  }

  /**
   * Handle variant:update events to sync our swatches when variant changes externally
   */
  handleVariantUpdate(event) {
    // The event contains the new HTML, we could re-init from it
    // But simpler: just re-sync based on the URL variant parameter
    const url = new URL(window.location.href);
    const variantId = url.searchParams.get('variant');

    if (!variantId) return;

    // Find our color selector and update it based on which variant is now selected
    document.querySelectorAll(this.selectors.colorSelector).forEach((selector) => {
      const matchingSwatch = selector.querySelector(`[data-variant-id="${variantId}"]`);

      if (matchingSwatch) {
        this.updateActiveState(selector, matchingSwatch);
        this.updateColorDisplay(selector, matchingSwatch.dataset.color);
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ColorSwatches());
} else {
  new ColorSwatches();
}

// Re-initialize after Shopify section rendering (for AJAX section updates)
document.addEventListener('shopify:section:load', () => {
  new ColorSwatches();
});
